import { appConfigStore } from 'Models/AppConfigStore';
import { bookingType } from 'Models/Enums';
import { store } from 'Models/Store';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { getMessageByKey, getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import { BookingOrderByOption } from 'Util/_HumanWritten/Bookings/FetchBookingsUtils';
import { camelCase } from 'Util/StringUtils';
import { BookingStatusTagOption } from 'Views/Components/_HumanWritten/Bookings/BookingStatusTag';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useDynamicBookingFieldsStore } from 'Stores/DynamicBookingFieldsStore';
import { BookingColumnKeys } from './BookingColumnKeys';

export interface ColumnConfig {
	key: string;
	label: string;
	visible: boolean;
	locked?: boolean; // For columns that can't be hidden (like checkbox)
}

// Column keys for admin-configured dynamic booking fields are namespaced so they
// can be told apart from the static columns and mapped back to a field id.
export const DYNAMIC_FIELD_COLUMN_PREFIX = 'dynamicField:';

export const isDynamicFieldColumnKey = (key: string) => key.startsWith(DYNAMIC_FIELD_COLUMN_PREFIX);

export const dynamicFieldIdFromColumnKey = (key: string) => key.slice(DYNAMIC_FIELD_COLUMN_PREFIX.length);

// One column per active (non-archived) dynamic field so each can be toggled and
// exported individually. Sourced from the cached dynamic-field store, so the
// columns appear once the fields have loaded (see rebuildColumns).
const getDynamicFieldColumns = (): ColumnConfig[] => {
	const { fields } = useDynamicBookingFieldsStore.getState();
	return fields
		.filter(f => !f.archived)
		.slice()
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
		.map(f => ({
			key: `${DYNAMIC_FIELD_COLUMN_PREFIX}${f.id}`,
			label: f.label,
			visible: false,
		}));
};

export type TravelStatusOption = 'Active' | 'Travelled' | 'Cancelled';

export interface StaffBookingsFilters {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate: boolean;
	bookingFilterType: string;
	bookingTypes: bookingType[];
	travelStatusFilters: TravelStatusOption[];
	// Selected route ids. Empty means "all routes" — no clause is sent, so an all-selected
	// popover and an empty one are the same request (and both read as inactive in
	// countActiveFilters). Routes are directional, so one entry is one leg.
	routeFilters: string[];
	orderBy: BookingOrderByOption;
	orderByDescending: boolean;
	pageNo: number;
	statusFilters: BookingStatusTagOption[];
}

// Store a simplified version for persistence
interface ColumnVisibility {
	[key: string]: boolean;
}

interface StaffBookingsState {
	// Current filters
	filters: StaffBookingsFilters;

	// Selected booking IDs (for checkboxes)
	selectedBookingIds: Set<string>;

	// UI state
	activeTab: string;
	selectedStatuses: BookingStatusTagOption[];
	bookingTypes: bookingType[];
	travelStatusFilters: TravelStatusOption[];
	selectedRoutes: string[];
	filterByCreatedDate: boolean;

	// Context tracking for smart resets
	currentContext: string | null;

	// Columns - only persist visibility, not full configs
	columnVisibility: ColumnVisibility;
	visibleColumns: ColumnConfig[]; // Computed, not persisted

	setVisibleColumns: (columns: ColumnConfig[]) => void;
	toggleColumn: (key: string) => void;
	rebuildColumns: () => void;

	// Actions
	setFilters: (filters: StaffBookingsFilters) => void;
	updateFilters: (partialFilters: Partial<StaffBookingsFilters>) => void;
	resetFilters: (onResultsPage: boolean) => void;
	setSelectedBookingIds: (ids: Set<string>) => void;
	setActiveTab: (tab: string) => void;
	setSelectedStatuses: (statuses: BookingStatusTagOption[]) => void;
	setSearchString: (searchString?: string) => void;
	setBookingTypes: (bookingTypes: bookingType[]) => void;
	setTravelStatusFilters: (travelStatusFilters: TravelStatusOption[]) => void;
	setSelectedRoutes: (routeFilters: string[]) => void;
	setFilterByCreatedDate: (filterByCreatedDate: boolean) => void;
	setContext: (context: string | null) => void;

	// Helper methods
	adjustDatesForTab: (tab: string, currentFilters: StaffBookingsFilters) => Partial<StaffBookingsFilters>;
}

const defaultSelectedStatuses = getStatusToShow().filter(x => x !== BookingStatusTagOption.CANCELLED);
const defaultBookingTypes = getBookingTypesInApp();
const defaultTravelStatusFilters: TravelStatusOption[] = ['Active', 'Travelled', 'Cancelled'];

// `searchString` defaults to '' rather than being omitted: react-query hashes query keys with
// JSON.stringify, which drops undefined properties, so an absent search string and an empty one
// would hash to two different cache entries for the same view.
export const defaultFilters: StaffBookingsFilters = {
	searchString: '',
	bookingFilterType: 'Active',
	bookingTypes: defaultBookingTypes,
	travelStatusFilters: defaultTravelStatusFilters,
	routeFilters: [],
	statusFilters: defaultSelectedStatuses,
	orderBy: BookingOrderByOption.CREATED_DATE,
	orderByDescending: true,
	pageNo: 0,
	filterByCreatedDate: false,
};

const defaultUserBookingsFilters: StaffBookingsFilters = {
	searchString: '',
	bookingFilterType: 'Active',
	bookingTypes: defaultBookingTypes,
	travelStatusFilters: defaultTravelStatusFilters,
	routeFilters: [],
	statusFilters: defaultSelectedStatuses,
	orderBy: BookingOrderByOption.TRIP_DATE,
	orderByDescending: false,
	pageNo: 0,
	filterByCreatedDate: false,
};

export function getBookingTypesInApp() {
	const bookingTypes: bookingType[] = [];
	if (appConfigStore.passengerOnlyBookingEnabled) {
		bookingTypes.push('PASSENGER_ONLY');
	}
	if (appConfigStore.vehicleEnabled) {
		bookingTypes.push('VEHICLE');
	}
	if (appConfigStore.freightEnabled) {
		bookingTypes.push('FREIGHT');
	}
	if (appConfigStore.eventsEnabled) {
		bookingTypes.push('EVENT_BOOKING');
	}
	return bookingTypes;
}

export function getStatusToShow() {
	const statuses: BookingStatusTagOption[] = Object.values(BookingStatusTagOption).filter(
		x => x !== BookingStatusTagOption.UNPAID,
	);

	// Show the Unpaid status when either creating unpaid bookings is allowed, or the dedicated
	// "show unpaid status" setting is on (e.g. to keep existing unpaid bookings visible/filterable
	// after new pay-later bookings have been disabled).
	if (appConfigStore.ShowUnpaidStatusTag) {
		statuses.push(BookingStatusTagOption.UNPAID);
	}

	return statuses;
}

export function getBookingTypeDisplayName(typeOfBooking: bookingType) {
	switch (typeOfBooking) {
		case 'PASSENGER_ONLY':
			return 'Passenger only';
		case 'VEHICLE':
			return getPascalCaseMessageByKey('vehicle__name__label', 'Vehicle');
		case 'FREIGHT':
			return getPascalCaseMessageByKey('freight__name__label', 'Freight');
		case 'EVENT_BOOKING':
			return 'Event';
		default:
			return typeOfBooking;
	}
}

// Default columns configuration
const getDefaultColumns = (): ColumnConfig[] => [
	{
		key: BookingColumnKeys.BOOKING_ID,
		label: 'Booking ID',
		visible: true,
		locked: true,
	},
	{ key: BookingColumnKeys.CREATED_DATE, label: 'Created Date', visible: true },
	// Created By names the staff member who took the booking, so it is staff-only like Vessel.
	...(store.isStaff ? [{ key: BookingColumnKeys.CREATED_BY, label: 'Created By', visible: false }] : []),
	...(store.isStaff ? [{ key: BookingColumnKeys.ALTERATION_COUNT, label: 'Times Altered', visible: false }] : []),
	...(store.isStaff ? [{ key: BookingColumnKeys.CUSTOMER, label: 'Customer', visible: true }] : []),
	{ key: BookingColumnKeys.TYPE, label: 'Type', visible: true },
	...(appConfigStore.eventsEnabled
		? [
				{ key: BookingColumnKeys.EVENT_NAME, label: 'Event Name', visible: false },
				{ key: BookingColumnKeys.EVENT_TICKETS, label: 'Event Tickets', visible: false },
				{ key: BookingColumnKeys.EVENT_PRIMARY_CONTACT, label: 'Event Contact', visible: false },
			]
		: []),
	{ key: BookingColumnKeys.DIRECTION, label: 'Direction', visible: true },
	{ key: BookingColumnKeys.TRIP_DATE, label: 'Trip Date', visible: true },
	// Vessel (ferry name) is staff-only — hidden from invoiced / fixed-price users.
	...(store.isStaff ? [{ key: BookingColumnKeys.VESSEL, label: 'Vessel', visible: false }] : []),
	...(appConfigStore.vehicleEnabled
		? [
				{ key: BookingColumnKeys.VEHICLE_MAKE_MODEL, label: 'Vehicle Type', visible: false },
				{ key: BookingColumnKeys.VEHICLE_LENGTH, label: 'Vehicle Length', visible: false },
				{ key: BookingColumnKeys.VEHICLE_WEIGHT, label: 'Vehicle Weight', visible: false },
				// Width columns only when the width feature is enabled.
				...(appConfigStore.vehicleWidthEnabled
					? [{ key: BookingColumnKeys.VEHICLE_WIDTH, label: 'Vehicle Width', visible: false }]
					: []),
				{ key: BookingColumnKeys.VEHICLE_CHARACTERISTICS, label: 'Vehicle Characteristics', visible: false },
				{
					key: BookingColumnKeys.REGO,
					label: getMessageByKey('vehicle-tab__rego__label', 'Rego'),
					visible: false,
				},
				{ key: BookingColumnKeys.DRIVER_CONTACT, label: 'Driver Contact', visible: false },
			]
		: []),
	...(appConfigStore.trailersEnabled
		? [
				{ key: BookingColumnKeys.TRAILER_TYPE, label: 'Trailer Type', visible: false },
				{ key: BookingColumnKeys.TRAILER_LENGTH, label: 'Trailer Length', visible: false },
				{ key: BookingColumnKeys.TRAILER_WEIGHT, label: 'Trailer Weight', visible: false },
				...(appConfigStore.vehicleWidthEnabled
					? [{ key: BookingColumnKeys.TRAILER_WIDTH, label: 'Trailer Width', visible: false }]
					: []),
				{ key: BookingColumnKeys.TRAILER_CHARACTERISTICS, label: 'Trailer Characteristics', visible: false },
			]
		: []),
	{ key: BookingColumnKeys.PASSENGERS, label: 'Passengers', visible: false },
	...(appConfigStore.passengerInfoEnabled
		? [{ key: BookingColumnKeys.PASSENGER_NAMES, label: 'Passenger Names', visible: false }]
		: []),
	...(appConfigStore.passengerClassesEnabled
		? [
				{ key: BookingColumnKeys.PASSENGER_CLASSES, label: 'Fare Types', visible: false },
				{ key: BookingColumnKeys.SEATS, label: 'Seats', visible: false },
			]
		: []),
	...(appConfigStore.vehicleNoteEnabled || appConfigStore.passengerNoteEnabled
		? [{ key: BookingColumnKeys.ADDITIONAL_INFORMATION, label: 'Additional Information', visible: false }]
		: []),
	...getDynamicFieldColumns(),
	...(appConfigStore.freightEnabled
		? [
				{ key: BookingColumnKeys.FREIGHT_DESCRIPTION, label: 'Freight Description', visible: false },
				{ key: BookingColumnKeys.FREIGHT_DIMENSIONS, label: 'Freight Dimensions', visible: false },
			]
		: []),
	...(appConfigStore.freightEnabled && appConfigStore.freightReceiverDetailsEnabled
		? [{ key: BookingColumnKeys.FREIGHT_RECEIVER_DETAILS, label: 'Freight Receiver', visible: false }]
		: []),
	...(appConfigStore.freightEnabled && appConfigStore.freightNotifyDetailsEnabled
		? [{ key: BookingColumnKeys.FREIGHT_NOTIFY_DETAILS, label: 'Freight Notify', visible: false }]
		: []),
	{ key: BookingColumnKeys.ADD_ONS, label: 'Add Ons', visible: false },
	{ key: BookingColumnKeys.AMOUNT, label: 'Amount', visible: true },
	{ key: BookingColumnKeys.STATUS, label: 'Status', visible: true },
	{ key: BookingColumnKeys.TRAVEL_STATUS, label: 'Travel Status', visible: false },
	...(appConfigStore.cancellationReasonsEnabled
		? [{ key: BookingColumnKeys.CANCELLATION_REASON, label: 'Cancellation Reason', visible: false }]
		: []),
	...(appConfigStore.systemWasMigrated
		? [{ key: BookingColumnKeys.LEGACY_ID, label: 'Legacy ID', visible: false }]
		: []),
];

// Helper to merge visibility with default columns
const buildColumns = (visibility: ColumnVisibility): ColumnConfig[] => {
	const defaults = getDefaultColumns();

	return defaults.map(col => ({
		...col,
		visible: visibility[col.key] ?? col.visible,
	}));
};

// Custom serializer to handle Set and Date objects
const customStorage = {
	getItem: (name: string) => {
		const str = sessionStorage.getItem(name);
		if (!str) return null;

		const parsed = JSON.parse(str);

		// Convert selectedBookingIds array back to Set
		if (parsed.state?.selectedBookingIds && Array.isArray(parsed.state.selectedBookingIds)) {
			parsed.state.selectedBookingIds = new Set(parsed.state.selectedBookingIds);
		}

		// Convert date strings back to Date objects
		if (parsed.state?.filters?.startDate) {
			parsed.state.filters.startDate = new Date(parsed.state.filters.startDate);
		}
		if (parsed.state?.filters?.endDate) {
			parsed.state.filters.endDate = new Date(parsed.state.filters.endDate);
		}

		return str;
	},
	setItem: (name: string, value: string) => {
		const parsed = JSON.parse(value);

		// Convert Set to array for serialization
		if (parsed.state?.selectedBookingIds instanceof Set) {
			parsed.state.selectedBookingIds = Array.from(parsed.state.selectedBookingIds);
		}

		sessionStorage.setItem(name, JSON.stringify(parsed));
	},
	removeItem: (name: string) => sessionStorage.removeItem(name),
};

// Bump this whenever the persisted shape / default columns change so existing saved stores are
// invalidated (a new key means old preferences are ignored and defaults are rebuilt). v2: added
// the staff-only Vessel column.
const STORAGE_KEY_VERSION = 'v2';

const getStorageKey = (): string => {
	const clientName = camelCase(whiteLabelStore.config.companyName);
	return `${clientName} : booking-filter-preferences : ${STORAGE_KEY_VERSION}`;
};

export const useStaffBookingsStore = create<StaffBookingsState>()(
	persist(
		(set, get) => ({
			// Initial state
			filters: defaultFilters,
			selectedBookingIds: new Set<string>(),
			activeTab: 'Active',
			selectedStatuses: defaultSelectedStatuses,
			bookingTypes: defaultBookingTypes,
			travelStatusFilters: defaultTravelStatusFilters,
			selectedRoutes: [],
			filterByCreatedDate: false,
			currentContext: null,

			// Columns
			columnVisibility: {},
			visibleColumns: getDefaultColumns(),

			setVisibleColumns: (columns: ColumnConfig[]) => {
				const visibility: ColumnVisibility = {};
				columns.forEach(col => {
					visibility[col.key] = col.visible;
				});
				set({
					visibleColumns: columns,
					columnVisibility: visibility,
				});
			},

			// Rebuilds the column list from the current defaults (which now include
			// any dynamic-field columns) while preserving persisted visibility. Called
			// once the dynamic fields finish loading so their columns appear.
			rebuildColumns: () => {
				set(state => ({ visibleColumns: buildColumns(state.columnVisibility) }));
			},

			toggleColumn: (key: string) => {
				const currentColumns = get().visibleColumns;
				const updatedColumns = currentColumns.map(col =>
					col.key === key && !col.locked ? { ...col, visible: !col.visible } : col,
				);

				const visibility: ColumnVisibility = {};
				updatedColumns.forEach(col => {
					visibility[col.key] = col.visible;
				});

				set({
					visibleColumns: updatedColumns,
					columnVisibility: visibility,
				});
			},

			// Actions
			setFilters: (filters: StaffBookingsFilters) => {
				set({
					filters,
					selectedStatuses: filters.statusFilters,
					bookingTypes: filters.bookingTypes,
					travelStatusFilters: filters.travelStatusFilters,
					selectedRoutes: filters.routeFilters,
					activeTab: filters.bookingFilterType,
					filterByCreatedDate: filters.filterByCreatedDate,
				});
			},

			updateFilters: (partialFilters: Partial<StaffBookingsFilters>) => {
				const currentFilters = get().filters;
				const newFilters = { ...currentFilters, ...partialFilters };

				set({
					filters: newFilters,
					// Sync UI state with filters
					selectedStatuses: newFilters.statusFilters,
					bookingTypes: newFilters.bookingTypes,
					travelStatusFilters: newFilters.travelStatusFilters,
					selectedRoutes: newFilters.routeFilters,
					activeTab: newFilters.bookingFilterType,
					filterByCreatedDate: newFilters.filterByCreatedDate,
				});
			},

			resetFilters: (onResultsPage: boolean) => {
				const resetFilters = onResultsPage ? defaultUserBookingsFilters : defaultFilters;
				set({
					filters: resetFilters,
					selectedBookingIds: new Set<string>(),
					activeTab: resetFilters.bookingFilterType,
					selectedStatuses: resetFilters.statusFilters,
					bookingTypes: resetFilters.bookingTypes,
					travelStatusFilters: resetFilters.travelStatusFilters,
					selectedRoutes: resetFilters.routeFilters,
					filterByCreatedDate: resetFilters.filterByCreatedDate,
				});
			},

			setSelectedBookingIds: (ids: Set<string>) => {
				set({ selectedBookingIds: ids });
			},

			setSearchString: (searchString?: string) => {
				const currentFilters = get().filters;

				set({
					filters: {
						...currentFilters,
						// Normalised so the query key can only ever take one shape (see defaultFilters).
						searchString: searchString ?? '',
						pageNo: 0,
					},
				});
			},

			setContext: (currentContext: string | null) => {
				set({ currentContext });
			},

			setFilterByCreatedDate: (filterByCreatedDate: boolean) => {
				const currentFilters = get().filters;

				set({
					filterByCreatedDate,
					filters: {
						...currentFilters,
						filterByCreatedDate,
						pageNo: 0,
					},
				});
			},

			setActiveTab: (tab: string) => {
				const currentFilters = get().filters;
				const currentStatuses = get().selectedStatuses;
				let newStatuses = [...currentStatuses];

				// Handle status adjustments based on tab
				if (tab === 'Active') {
					// Remove cancelled status when switching to Active tab
					if (currentStatuses.includes(BookingStatusTagOption.CANCELLED)) {
						newStatuses = currentStatuses.filter(status => status !== BookingStatusTagOption.CANCELLED);

						// If we removed all statuses, set to all available active statuses
						if (newStatuses.length === 0) {
							newStatuses = Object.values(BookingStatusTagOption).filter(
								status => status !== BookingStatusTagOption.CANCELLED,
							);
						}
					}
				} else if (tab === 'Past/Cancelled') {
					// Add cancelled status when switching to Past/Cancelled tab (if not already present)
					if (!currentStatuses.includes(BookingStatusTagOption.CANCELLED)) {
						newStatuses = [...currentStatuses, BookingStatusTagOption.CANCELLED];
					}
				}

				// Handle date adjustments
				const dateAdjustments = get().adjustDatesForTab(tab, currentFilters);

				// Update everything at once
				set({
					activeTab: tab,
					selectedStatuses: newStatuses,
					filters: {
						...currentFilters,
						bookingFilterType: tab,
						statusFilters: newStatuses,
						pageNo: 0,
						...dateAdjustments,
					},
				});
			},

			setBookingTypes: (newBookingTypes: bookingType[]) => {
				const currentFilters = get().filters;

				set({
					bookingTypes: newBookingTypes,
					filters: {
						...currentFilters,
						bookingTypes: newBookingTypes,
						pageNo: 0,
					},
				});
			},

			setTravelStatusFilters: (newTravelStatusFilters: TravelStatusOption[]) => {
				const currentFilters = get().filters;

				set({
					travelStatusFilters: newTravelStatusFilters,
					filters: {
						...currentFilters,
						travelStatusFilters: newTravelStatusFilters,
						pageNo: 0,
					},
				});
			},

			setSelectedRoutes: (newRouteFilters: string[]) => {
				const currentFilters = get().filters;

				set({
					selectedRoutes: newRouteFilters,
					filters: {
						...currentFilters,
						routeFilters: newRouteFilters,
						pageNo: 0,
					},
				});
			},

			setSelectedStatuses: (statuses: BookingStatusTagOption[]) => {
				const currentFilters = get().filters;

				// Status filtering operates WITHIN the current tab — selecting a status no longer
				// switches tabs. This is what lets you filter the Past/Cancelled (or All) tab by
				// Awaiting/Invoiced and surface a cancelled booking that owes a cancellation fee.
				// The old behaviour recomputed the tab from the selected statuses and forced the
				// 'Active' tab (which excludes cancelled bookings) whenever a non-cancelled status
				// like Awaiting was picked, so those bookings could never appear. The tab is still
				// changed explicitly via setActiveTab (the tab control).
				set({
					selectedStatuses: statuses,
					filters: {
						...currentFilters,
						statusFilters: statuses,
						pageNo: 0,
					},
				});
			},

			// Helper methods
			adjustDatesForTab: (tab: string, currentFilters: StaffBookingsFilters) => {
				const today = new Date();
				today.setHours(0, 0, 0, 0);

				const adjustments: Partial<StaffBookingsFilters> = {};

				// Only adjust dates if filtering by trip date (not created date)
				if (currentFilters.filterByCreatedDate) {
					return adjustments;
				}

				if (tab === 'Active') {
					// For Active bookings, dates should be today or in the future

					// Adjust start date if it's in the past
					if (currentFilters.startDate && currentFilters.startDate < today) {
						adjustments.startDate = new Date(today);
					}

					// Adjust end date if it's in the past
					if (currentFilters.endDate && currentFilters.endDate < today) {
						const futureDate = new Date(today);
						futureDate.setDate(futureDate.getDate() + 30);
						adjustments.endDate = futureDate;
					}

					// If start date is after end date, adjust end date
					const startDate = adjustments.startDate || currentFilters.startDate;
					const endDate = adjustments.endDate || currentFilters.endDate;
					if (startDate && endDate && startDate > endDate) {
						const newEndDate = new Date(startDate);
						newEndDate.setDate(newEndDate.getDate() + 30);
						adjustments.endDate = newEndDate;
					}
				} else if (tab === 'Past/Cancelled') {
					// For Past/Cancelled bookings, dates should be in the past or today

					// Adjust start date if it's in the future
					if (currentFilters.startDate && currentFilters.startDate > today) {
						const pastDate = new Date(today);
						pastDate.setDate(pastDate.getDate() - 30);
						adjustments.startDate = pastDate;
					}

					// Adjust end date if it's in the future
					if (currentFilters.endDate && currentFilters.endDate > today) {
						adjustments.endDate = new Date(today);
					}

					// If start date is after end date, adjust start date
					const startDate = adjustments.startDate || currentFilters.startDate;
					const endDate = adjustments.endDate || currentFilters.endDate;
					if (startDate && endDate && startDate > endDate) {
						const newStartDate = new Date(endDate);
						newStartDate.setDate(newStartDate.getDate() - 30);
						adjustments.startDate = newStartDate;
					}
				}

				return adjustments;
			},
		}),
		{
			name: getStorageKey(),
			storage: createJSONStorage(() => customStorage),
			partialize: state => ({
				filters: state.filters,
				activeTab: state.activeTab,
				selectedStatuses: state.selectedStatuses,
				bookingTypes: state.bookingTypes,
				travelStatusFilters: state.travelStatusFilters,
				selectedRoutes: state.selectedRoutes,
				filterByCreatedDate: state.filterByCreatedDate,
				currentContext: state.currentContext,
				columnVisibility: state.columnVisibility, // Only persist visibility
				// visibleColumns is NOT persisted - it's rebuilt on load
			}),
			onRehydrateStorage: () => state => {
				if (state) {
					// Ensure selectedBookingIds is always a Set (not persisted)
					state.selectedBookingIds = new Set<string>();

					// Ensure all state is in sync after hydration
					if (state.filters) {
						// Entries persisted before searchString was defaulted to '' would otherwise
						// rehydrate as undefined and split the query key in two (see defaultFilters).
						state.filters.searchString = state.filters.searchString ?? '';
						state.selectedStatuses = state.filters.statusFilters || defaultSelectedStatuses;
						state.bookingTypes = state.filters.bookingTypes || defaultBookingTypes;
						state.travelStatusFilters = state.filters.travelStatusFilters || defaultTravelStatusFilters;
						// Entries persisted before the route filter existed rehydrate without this key.
						// Defaulting it here (rather than bumping STORAGE_KEY_VERSION) keeps everyone's
						// saved column preferences, and an undefined value would split the query key.
						state.filters.routeFilters = state.filters.routeFilters ?? [];
						state.selectedRoutes = state.filters.routeFilters;
						state.activeTab = state.filters.bookingFilterType || 'Active';
						state.filterByCreatedDate = state.filters.filterByCreatedDate ?? false;
					}

					// Rebuild columns with fresh configs and persisted visibility
					state.visibleColumns = buildColumns(state.columnVisibility);
				}
			},
		},
	),
);

/**
 * Holds the /staff-bookings filters while the user is off on a customer's account page.
 *
 * The user-detail page embeds this same table (`onResultsPage`), and that embedded copy shares this
 * singleton store — and its sessionStorage entry — with the main table. Mounting it forces the
 * user-bookings defaults, which would otherwise destroy the filters the main table is supposed to
 * restore when you come back.
 *
 * Restoring on the embedded table's unmount does not work: React renders and commits the incoming
 * route before running the outgoing route's cleanup, so the main table would already have built its
 * first query key from the embedded defaults and would then fetch a second time. Instead the
 * embedded table hands the filters over on the way in, and the main table collects them on the way
 * out — both during render, before either builds a query key.
 */
let stashedMainBookingFilters: StaffBookingsFilters | null = null;

/**
 * Called by the embedded (user-detail) table before it applies its own defaults.
 *
 * Write-once until consumed, and that is the whole point rather than an optimisation: the user-detail
 * page keys its inner component on the user query's `dataUpdatedAt` (a deliberate remount, so the
 * edit session rebuilds on fresh data), so this table remounts whenever that query settles again — on
 * a background refetch of already-cached data, and on every save or cancel. Each remount gets a fresh
 * latch and calls this again, by which time the live filters are the defaults the *previous* mount
 * applied. Only the first call of a visit has the main table's values, so later ones must not
 * overwrite it. Consumed on the way back out, which re-arms it for the next visit.
 */
export function stashMainBookingFilters(): void {
	if (stashedMainBookingFilters !== null) {
		return;
	}

	stashedMainBookingFilters = useStaffBookingsStore.getState().filters;
}

/** Called by the main table when it is resetting anyway, so the stash cannot go stale. */
export function clearStashedMainBookingFilters(): void {
	stashedMainBookingFilters = null;
}

/** Called by the main table when it is restoring rather than resetting. */
export function restoreStashedMainBookingFilters(): void {
	if (stashedMainBookingFilters === null) {
		return;
	}
	useStaffBookingsStore.getState().setFilters(stashedMainBookingFilters);
	stashedMainBookingFilters = null;
}
